/* eslint-disable no-console */
import ApplicationConf from 'conf/ApplicationConf'
import { keyBy, uniq } from 'lodash'
import { toast } from 'react-toastify'
import { createAppSlice } from 'common/createAppSlice'
import { aquaFetch } from 'common/utils/ActionUtils'
import { STATUS } from 'common/constants/storeConstants'

const initialState = {
    status: STATUS.IDLE,
    byId: {},
    allIds: [],
    all: [],
}

const CustomersSlice = createAppSlice({
    name: 'gdl/customers',
    initialState,
    reducers: (create) => ({
        reset: create.reducer(() => initialState),

        createCustomer: create.asyncThunk(
            async (customer, { rejectWithValue }) => {
                try {
                    const response = await aquaFetch(ApplicationConf.gdl.customers(), { body: customer, method: 'POST' })
                    toast.success('Client créé avec succès')
                    return response
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors de la création d\'un client')
                    return rejectWithValue(err)
                }
            },
            {
                fulfilled: state => {
                    state.status = STATUS.IDLE
                },
            },
        ),
        updateCustomer: create.asyncThunk(
            async (customer, { rejectWithValue }) => {
                try {
                    const response = await aquaFetch(ApplicationConf.gdl.customers(), { body: customer, method: 'PUT' })
                    toast.success('Client modifié avec succès')
                    return response
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors de la modification d\'un client')
                    return rejectWithValue(err)
                }
            },
            {
                fulfilled: state => {
                    state.status = STATUS.IDLE
                },
            },
        ),
        deleteCustomer: create.asyncThunk(
            async (id, { rejectWithValue }) => {
                try {
                    const response = await aquaFetch(ApplicationConf.gdl.customer(id), { method: 'DELETE' })
                    toast.success('Client supprimé avec succès')
                    return response
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors de la suppression d\'un Client')
                    return rejectWithValue(err)
                }
            },
            {
                fulfilled: state => {
                    state.status = STATUS.IDLE
                },
            },
        ),
        fetchCustomers: create.asyncThunk(
            async (_, { rejectWithValue }) => {
                try {
                    return await aquaFetch(ApplicationConf.gdl.customers())
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors du chargement des clients')
                    return rejectWithValue(err)
                }
            },
            {
                pending: state => {
                    state.status = STATUS.LOADING
                },
                rejected: state => {
                    state.status = STATUS.REJECTED
                },
                fulfilled: (state, action) => {
                    state.status = STATUS.SUCCEEDED
                    const byId = keyBy(action.payload, 'id')
                    state.byId = byId
                    state.allIds = action.payload.map(customer => customer.id)
                    state.all = action.payload
                },
                options: {
                    condition: (_, { getState }) => {
                        const { gdl } = getState()
                        return gdl.customers.status === STATUS.IDLE
                    },
                    dispatchConditionRejected: true,
                },
            },
        ),
        fetchStatsSynthesis: create.asyncThunk(
            async ({ instanceIds }, { rejectWithValue }) => {
                try {
                    return await aquaFetch(ApplicationConf.gdl.customerStats(), {
                        method: 'POST',
                        body: { instanceIds, group: 'SYNTHESIS', action: 'ALL' },
                    })
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors du chargement des statistiques')
                    return rejectWithValue(err)
                }
            },
            {
                pending: state => {
                    state.statusStatsSynthesis = STATUS.LOADING
                },
                rejected: state => {
                    state.statusStatsSynthesis = STATUS.REJECTED
                },
                fulfilled: (state, action) => {
                    state.statsSynthesis = action.payload
                    state.statusStatsSynthesis = STATUS.SUCCEEDED
                },
            },
        ),
        fetchStats: create.asyncThunk(
            async ({ instanceIds, action, group }, { rejectWithValue }) => {
                try {
                    return await aquaFetch(ApplicationConf.gdl.customerStats(), {
                        method: 'POST',
                        body: { instanceIds, group, action },
                    })
                } catch (err) {
                    console.error(err)
                    toast.error('Erreur lors du chargement des statistiques')
                    return rejectWithValue(err)
                }
            },
            {
                pending: state => {
                    state.statusStats = STATUS.LOADING
                },
                rejected: state => {
                    state.statusStats = STATUS.REJECTED
                },
                fulfilled: (state, action) => {
                    state.stats = action.payload
                    state.statusStats = STATUS.SUCCEEDED
                },
            },
        ),
    }),
    selectors: {
        selectCustomers: (state) => state.allIds.map(id => state.byId[id]),
        selectUnlinkInstanceIds: (state) => uniq(state.allIds.flatMap(id => state.byId[id]?.instances.map(i => i.instance) ?? [])),
    },
})

export const { fetchCustomers, createCustomer, updateCustomer, deleteCustomer, reset } = CustomersSlice.actions
export const {
    selectCustomers,
    selectUnlinkInstanceIds,
} = CustomersSlice.getSelectors((state) => state.gdl.customers)
export default CustomersSlice